<template>
  <div class="rd-screen">
    <report-tools :copy="rows.length > 0" @copy="copyTable"></report-tools>

    <report-state v-if="busy" kind="loading"></report-state>
    <report-state
      v-else-if="!rows.length"
      icon="mdi-package-variant-closed"
      :title="emptyTitle"
      :text="emptyText"
    ></report-state>

    <ui-table
      v-else
      variant="report"
      wrap-class="rd-card--scroll"
      :columns="columns"
      :rows="rows"
      :row-key="(row) => row.key"
      :selected="isOpen"
      @open="openOrder"
    >
      <template #cell-worker="{ row }">
        <span class="rd-person">
          <span class="ui-avatar ui-avatar--neutral ui-avatar--sm">{{ row.initials }}</span>
          <span class="rd-person__name">{{ row.worker }}</span>
        </span>
      </template>

      <template #cell-shifts="{ row }">
        <span class="mp-shifts">
          <report-shift-tag v-for="id in row.shiftIds" :key="id" :shift="id"></report-shift-tag>
        </span>
      </template>

      <template #cell-meals="{ row }">
        <span v-for="(meal, mealIndex) in row.meals" :key="mealIndex" class="mp-meals__item">
          <b class="rd-num">{{ meal.quantity }}×</b> {{ meal.name }}
          <report-shift-tag v-if="row.mixedShifts && meal.shiftNumber" :shift="meal.shiftNumber"></report-shift-tag>
        </span>
      </template>
    </ui-table>
  </div>
</template>

<script>
import CompanyMultiRestaurantTabPackages from "@/components/company/reports/multi-restaurant-tabs/CompanyMultiRestaurantTabPackages.vue";
import ReportShiftTag from "@/components/report/shared/ReportShiftTag.vue";
import { copyText, rowsToTsv } from "@/components/report/desktop/copyTable";
import ReportState from "@/components/report/desktop/ReportState.vue";
import ReportTools from "@/components/report/desktop/ReportTools.vue";
import hasMultiFeed from "../../../hasMultiFeed";
import CompanyDesktopMultiOrder from "./CompanyDesktopMultiOrder.vue";

const initialsOf = (name) =>
  name
    .split(" ")
    .filter(Boolean)
    .map((part) => part.charAt(0))
    .slice(0, 2)
    .join("");

/**
 * Pakovanje firme "više restorana" na računaru: narudžbe radnika po restoranu (šta je naručeno i u kojoj smeni).
 * Klik na red otvara detalje narudžbe (sa cenama) u panelu. Restorane, smene i period čita iz zajedničke trake, pretragu
 * (radnik, restoran ili obrok) takođe. Smena je posebna kolona (uz svaki obrok samo kad narudžba ima više smena).
 * Nasleđuje učitavanje, štampu i Excel iz starog taba.
 */
export default {
  name: "CompanyDesktopMultiPackages",
  extends: CompanyMultiRestaurantTabPackages,
  mixins: [hasMultiFeed],
  components: { ReportShiftTag, ReportState, ReportTools },
  inject: {
    shell: { from: "reportShell", default: null },
  },
  computed: {
    /** Kolone tabele: radnik, restoran, smena, obroci, (mjesto isporuke) i strelica. */
    columns() {
      return [
        { key: "#", label: "#", index: true },
        { key: "worker", label: this.$t("employee"), name: true },
        ...(this.hasBranches ? [{ key: "branch", label: this.$t("branch"), muted: true }] : []),
        { key: "restaurant", label: this.$t("restaurant"), muted: true },
        { key: "shifts", label: this.$t("shift.shift") },
        { key: "meals", label: this.$t("meals"), cellCls: "mp-meals" },
        ...(this.hasLocation ? [{ key: "location", label: this.$t("delivery_location"), muted: true }] : []),
        { key: "go", label: "", go: true },
      ];
    },
    busy() {
      return this.loading;
    },
    hasRows() {
      return this.rows.length > 0;
    },
    /** Narudžbe posle pretrage; redovi su samo za prikaz (štampa i Excel čitaju `orders`). */
    rows() {
      const search = this.toLatin(String(this.$store.state.report.filters.search || "")).toLowerCase().trim();
      return this.orders
        .map((order) => ({
          key: String(order.id),
          order,
          worker: this.toLatin(order.user_name || ""),
          initials: initialsOf(this.toLatin(order.user_name || "")),
          restaurant: order.restaurant,
          branch: order.company_name || "",
          shiftIds: this.shiftNumbers(order.meals),
          mixedShifts: this.shiftNumbers(order.meals).length > 1,
          location: this.toLatin(order.delivery_location || ""),
          meals: order.meals.map((meal) => ({
            quantity: meal.quantity,
            name: this.toLatin(meal.name || ""),
            shiftNumber: Number(this.formatShiftNumber(meal.shift)) || null,
          })),
        }))
        .filter(
          (row) =>
            !search ||
            [row.worker, row.branch, row.restaurant, row.location, ...row.meals.map((meal) => meal.name)]
              .join(" ")
              .toLowerCase()
              .includes(search)
        );
    },
    /** Mesto isporuke se prikazuje samo kad ga ijedna narudžba ima. */
    hasLocation() {
      return this.rows.some((row) => row.location);
    },
    emptyTitle() {
      const search = this.$store.state.report.filters.search;
      return search ? this.$t("report_mobile.no_results_for", { search }) : this.$t("no_orders");
    },
    emptyText() {
      return this.$store.state.report.filters.search ? "" : this.$t("no_orders_for_selected_date");
    },
    kpis() {
      const meals = this.rows.reduce((sum, row) => sum + row.meals.reduce((inner, meal) => inner + meal.quantity, 0), 0);
      return [
        { key: "orders", label: this.$t("orders"), value: this.rows.length, tone: "inverse" },
        { key: "meals", label: this.$t("report_desktop.total_meals"), value: meals },
        { key: "workers", label: this.$t("report_desktop.workers"), value: new Set(this.rows.map((row) => row.worker)).size },
      ];
    },
  },
  methods: {
    isOpen(row) {
      const top = this.shell?.panelStack?.[0];
      return Boolean(top && top.key === `order-${row.key}`);
    },
    /** Detalji narudžbe u panelu: radnik, restoran, obroci sa smenom i cenom. */
    openOrder(row, event) {
      this.$emit("open-panel", {
        opener: event?.currentTarget,
        key: `order-${row.key}`,
        root: true,
        title: row.worker,
        subtitle: [`#${row.order.id}`, this.hasBranches ? row.branch : "", row.restaurant].filter(Boolean).join(" · "),
        component: CompanyDesktopMultiOrder,
        props: { order: row.order },
      });
    },
    async copyTable() {
      const headers = [this.$t("employee"), ...(this.hasBranches ? [this.$t("branch")] : []), this.$t("restaurant"), this.$t("shift.shift"), this.$t("meals"), ...(this.hasLocation ? [this.$t("delivery_location")] : [])];
      const rows = this.rows.map((row) => [
        row.worker,
        ...(this.hasBranches ? [row.branch] : []),
        row.restaurant,
        row.shiftIds.join(", "),
        row.meals.map((meal) => `${meal.quantity}x ${meal.name}`).join(", "),
        ...(this.hasLocation ? [row.location] : []),
      ]);
      const copied = await copyText(rowsToTsv(headers, rows));
      this.$emit("toast", this.$t(copied ? "report_desktop.copied" : "report_desktop.copy_failed"));
    },
  },
};
</script>
